import { useState } from "react";

const theme = {
  primary: "#E8540A",
  primaryLight: "#FF6B2B",
  primaryDark: "#C44008",
  bg: "#FFFAF5",
  cardBg: "#FFFFFF",
  text: "#1A0A00",
  textMuted: "#5A3A20",
  border: "#F0D0B0",
  gradient: "linear-gradient(135deg, #a73901 0%, #df6920 100%)",
};

const icons = {
  authenticity: (
    <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
    </svg>
  ),
  knowledge: (
    <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
      <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/>
    </svg>
  ),
  devotion: (
    <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
    </svg>
  ),
  service: (
    <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  ),
};

const values = [
  {
    key: "authenticity",
    icon: icons.authenticity,
    title: "विश्वसनीयता",
    subtitle: "Authenticity",
    desc: "शुद्ध, शास्त्रबद्ध व प्रामाणिक संगीत आणि विधान सामग्री",
    featured: false,
  },
  {
    key: "knowledge",
    icon: icons.knowledge,
    title: "ज्ञान प्रसार",
    subtitle: "Knowledge Sharing",
    desc: "दिगंबर जैन परंपरा पुढील पिढीपर्यंत पोहोचवणे",
    featured: true,
  },
  {
    key: "devotion",
    icon: icons.devotion,
    title: "श्रद्धा",
    subtitle: "Devotion",
    desc: "भावपूर्ण आणि आध्यात्मिक अनुभव निर्माण करणे",
    featured: false,
  },
  {
    key: "service",
    icon: icons.service,
    title: "सेवा",
    subtitle: "Service",
    desc: "समाजाला सर्वोत्तम सेवा पुरवणे हे आमचे ध्येय",
    featured: false,
  },
];

export default function AboutPage() {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: theme.bg,
      fontFamily: "'Noto Sans Devanagari', 'Segoe UI', sans-serif",
      color: theme.text,
    }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700;800&family=Noto+Serif+Devanagari:wght@400;600;700&family=Playfair+Display:ital,wght@1,400&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .about-section { padding: 80px 24px 60px; max-width: 900px; margin: 0 auto; text-align: center; }

        .eyebrow {
          font-size: 18px;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: ${theme.primary};
          font-weight: 600;
          margin-bottom: 14px;
          font-family: 'Noto Sans Devanagari', sans-serif;
        }

        .hero-title {
          font-family: 'Noto Serif Devanagari', serif;
          font-size: clamp(36px, 6vw, 64px);
          font-weight: 700;
          color: ${theme.text};
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .title-divider {
          width: 60px;
          height: 3px;
          background: ${theme.primary};
          margin: 0 auto 48px;
          border-radius: 2px;
        }

        .mission-card {
          border: 1.5px solid ${theme.primary};
          border-radius: 16px;
          padding: 40px 48px;
          background: #fff;
          text-align: left;
          box-shadow: 0 4px 24px rgba(232,84,10,0.06);
          margin-bottom: 48px;
        }

        .mission-label {
          font-size: 17px;
          font-weight: 700;
          color: ${theme.primary};
          text-align: center;
          margin-bottom: 20px;
          font-family: 'Noto Serif Devanagari', serif;
        }

        .mission-body {
          font-size: 17px;
          line-height: 1.8;
          color: ${theme.text};
          margin-bottom: 20px;
        }

        .mission-body strong {
          font-weight: 700;
        }

        .mission-quote {
          font-size: 15px;
          color: ${theme.primary};
          font-style: italic;
          line-height: 1.7;
          border-left: 3px solid ${theme.primaryLight};
          padding-left: 16px;
          font-family: 'Noto Sans Devanagari', sans-serif;
        }

        .lang-highlight {
          font-size: 16px;
          color: ${theme.textMuted};
          line-height: 1.8;
          margin-top: 16px;
        }

        .lang-highlight strong {
          font-weight: 700;
          color: ${theme.text};
        }

        /* Values grid */
        .values-section {
          padding: 20px 24px 80px;
          max-width: 1050px;
          margin: 0 auto;
        }

        .values-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 24px;
        }

        .values-bottom {
          display: flex;
          justify-content: center;
        }

        .value-card {
          background: #fff;
          border: 1.5px solid ${theme.border};
          border-radius: 16px;
          padding: 36px 28px;
          text-align: center;
          cursor: default;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .value-card:hover, .value-card.featured {
          border-color: ${theme.primary};
          box-shadow: 0 8px 32px rgba(232,84,10,0.12);
          transform: translateY(-3px);
        }

        .value-card.featured {
          border-color: ${theme.primary};
        }

        .value-card.bottom-card {
          max-width: 340px;
          width: 100%;
        }

        .icon-circle {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: ${theme.gradient};
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          box-shadow: 0 4px 16px rgba(232,84,10,0.3);
        }

        .value-title {
          font-family: 'Noto Serif Devanagari', serif;
          font-size: 20px;
          font-weight: 700;
          color: ${theme.text};
          margin-bottom: 4px;
        }

        .value-subtitle {
          font-size: 13px;
          color: ${theme.primary};
          font-style: italic;
          margin-bottom: 14px;
          letter-spacing: 0.5px;
        }

        .value-desc {
          font-size: 14.5px;
          color: ${theme.textMuted};
          line-height: 1.7;
        }

        /* CTA Banner */
        .cta-banner {
          margin: 0 24px 80px;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
          border-radius: 20px;
          background: ${theme.gradient};
          padding: 52px 48px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .cta-banner::before {
          content: 'ॐ';
          position: absolute;
          right: 40px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 100px;
          color: rgba(255,255,255,0.12);
          font-family: 'Noto Serif Devanagari', serif;
          line-height: 1;
          pointer-events: none;
          user-select: none;
        }

        .cta-title {
          font-family: 'Noto Serif Devanagari', serif;
          font-size: clamp(20px, 3vw, 28px);
          font-weight: 700;
          color: #fff;
          margin-bottom: 14px;
        }

        .cta-sub {
          font-size: 15px;
          color: rgba(255,255,255,0.9);
          line-height: 1.7;
          max-width: 580px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .values-grid { grid-template-columns: 1fr; }
          .mission-card { padding: 28px 24px; }
          .cta-banner { padding: 40px 24px; }
          .cta-banner::before { display: none; }
        }

        @media (max-width: 600px) {
          .values-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* About Section */}
      <section className="about-section">
        <p className="eyebrow">आ म च्या  वि ष यी</p>
        <h1 className="hero-title">स्वरसागरामृत परिवार</h1>
        <div className="title-divider" />

        <div className="mission-card">
          <p className="mission-label">आमचा उद्देश</p>
          <p className="mission-body">
            <strong>स्वरसागरामृत परिवार</strong> ही केवळ एक वेबसाइट अथवा साधन नसून, दिगंबर जैन धर्मातील{" "}
            <strong>संगीत, पूजा, विधान, अभिषेक, मंत्रोचार</strong> आणि विधीपरंपरेचे जतन व प्रसार करण्याचा एक पवित्र संकल्प आहे.
          </p>
          <p className="mission-quote">
            "दिगंबर जैन धर्मातील पूजा, संगीत व विधान परंपरा पुढील पिढीपर्यंत शुद्ध, शास्त्रबद्ध व भावपूर्ण स्वरूपात पोहोचवणे"
          </p>
        </div>

        <p className="lang-highlight">
          आम्ही <strong>हिंदी, मराठी आणि कन्नड</strong> भाषांमध्ये संगीत पूजा, अभिषेक पाठ, स्तोत्र, जयमाला आणि विधान सामग्री संपूर्ण अर्थ, विधी माहिती व शुद्ध उच्चारणासह पुरवतो.
        </p>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="values-grid">
          {values.filter(v => v.key !== "service").map((val) => (
            <div
              key={val.key}
              className={`value-card${val.featured ? " featured" : ""}${hoveredCard === val.key ? " hovered" : ""}`}
              onMouseEnter={() => setHoveredCard(val.key)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="icon-circle">{val.icon}</div>
              <p className="value-title">{val.title}</p>
              <p className="value-subtitle">{val.subtitle}</p>
              <p className="value-desc">{val.desc}</p>
            </div>
          ))}
        </div>

        <div className="values-bottom">
          {values.filter(v => v.key === "service").map((val) => (
            <div
              key={val.key}
              className="value-card bottom-card"
              onMouseEnter={() => setHoveredCard(val.key)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="icon-circle">{val.icon}</div>
              <p className="value-title">{val.title}</p>
              <p className="value-subtitle">{val.subtitle}</p>
              <p className="value-desc">{val.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <div style={{ padding: "0 24px 80px", maxWidth: 1050, margin: "0 auto" }}>
        <div className="cta-banner">
          <p className="cta-title">ज्ञान, साधना, सेवा आणि श्रद्धा यांचे संगमस्थान</p>
          <p className="cta-sub">
            हे विशेषतः पंडित वर्ग, पूजा विधी करणारे श्रावक-श्राविका व संघांसाठी अत्यंत उपयुक्त ठरेल
          </p>
        </div>
      </div>
    </div>
  );
}